import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "@/lib/api";
import { type EventItem, getEventStatusRange, getHeroEvents } from "@/lib/events";

const STORAGE_KEY = "uasa-selected-event-id";

type EventContextValue = {
  events: EventItem[];
  selectedEvent: EventItem | null;
  selectedEventId: number | null;
  loading: boolean;
  selectEvent: (id: number) => void;
};

const EventContext = createContext<EventContextValue | null>(null);

function readStoredId() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const id = Number(raw);
    return Number.isFinite(id) ? id : null;
  } catch {
    return null;
  }
}

function pickDefaultEventId(events: EventItem[], storedId: number | null) {
  // Only restore a stored selection if it is an upcoming event
  if (storedId) {
    const stored = events.find((event) => event.id === storedId);
    if (stored && getEventStatusRange(stored) === "upcoming") return storedId;
  }
  // Default to the next upcoming event; never fall back to a past event
  return getHeroEvents(events)[0]?.id ?? null;
}

export function EventProvider({ children }: { children: ReactNode }) {
  const [events, setEvents] = useState<EventItem[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await api.get<EventItem[]>("/api/events");
        if (cancelled) return;
        const list = response.success && response.data ? response.data : [];
        setEvents(list);
        setSelectedEventId(pickDefaultEventId(list, readStoredId()));
      } catch {
        if (!cancelled) setEvents([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectEvent = (id: number) => {
    setSelectedEventId(id);
    try {
      localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      // ignore storage failures
    }
  };

  const selectedEvent = useMemo(
    () => events.find((event) => event.id === selectedEventId) ?? null,
    [events, selectedEventId],
  );

  const value = useMemo(
    () => ({
      events,
      selectedEvent,
      selectedEventId,
      loading,
      selectEvent,
    }),
    [events, selectedEvent, selectedEventId, loading],
  );

  return <EventContext.Provider value={value}>{children}</EventContext.Provider>;
}

export function useSelectedEvent() {
  const context = useContext(EventContext);
  if (!context) {
    throw new Error("useSelectedEvent must be used within EventProvider");
  }
  return context;
}
